var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Box, Button, IconButton, Paper, Popover, Skeleton, styled, SvgIcon, TextField, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateLeft } from "@fortawesome/pro-regular-svg-icons/faArrowRotateLeft";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faTimes } from "@fortawesome/pro-regular-svg-icons/faTimes";
import { faSparkles } from "@fortawesome/pro-regular-svg-icons/faSparkles";
import { faAngleLeft } from "@fortawesome/pro-regular-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons/faAngleRight";
import { intentExplanations, modificationInstructions } from "./AiTextEditingUtils";
export const AiTextEditingPopover = ({ anchorEl, handleClose, onReplace, intent, originalText, modification, getModifiedText, customPrompt }) => {
    const open = Boolean(anchorEl);
    const [curIndex, setCurIndex] = useState(-1);
    const [modifiedTextArray, setModifiedTextArray] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        void getText();
    }, []);
    const instructions = [
        `Purpose: This text will be used as a ${intent} in the video scene.`,
        `Context: ${intentExplanations[intent]}`,
        `Modification Requirements: ${modificationInstructions[modification]}`
    ].join("\n");
    const getText = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setIsLoading(true);
            const text = yield getModifiedText(originalText, customPrompt.trim() || instructions);
            setModifiedTextArray(prevTexts => {
                const updatedArray = [...prevTexts, text];
                setCurIndex(updatedArray.length - 1);
                return updatedArray;
            });
            setIsLoading(false);
        }
        catch (e) {
            setIsLoading(false);
        }
    });
    return (_jsx(Popover, { open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: { vertical: "bottom", horizontal: "left" }, children: _jsxs(GradientBorderPaper, { children: [_jsxs(Box, { sx: titleContainerSxProps, children: [_jsx(SvgIcon, { color: "gradient", fontSize: "small", children: _jsx(FontAwesomeIcon, { icon: faSparkles }) }), _jsx(Typography, { variant: "body1", children: modification }), _jsx(Box, { sx: { position: "absolute", right: "6px" }, children: _jsx(IconButton, { size: "small", color: "default", onClick: handleClose, children: _jsx(FontAwesomeIcon, { icon: faTimes }) }) }), modifiedTextArray.length > 1 &&
                            _jsxs(Box, { sx: sxPaginationArrows, children: [_jsx(IconButton, { size: "small", disabled: curIndex === 0, onClick: () => setCurIndex((prev) => prev - 1), children: _jsx(FontAwesomeIcon, { icon: faAngleLeft }) }), _jsx(Box, { children: `${curIndex + 1} / ${modifiedTextArray.length}` }), _jsx(IconButton, { size: "small", disabled: curIndex === modifiedTextArray.length - 1, onClick: () => setCurIndex((prev) => prev + 1), children: _jsx(FontAwesomeIcon, { icon: faAngleRight }) })] })] }), _jsx(Box, { sx: { padding: "8px 16px 0px" }, children: isLoading ?
                        _jsxs(Box, { height: "64px", sx: textSkeletonContainerSxProps, children: [_jsx(Skeleton, { variant: "rectangular", height: "12px", width: "100%" }), _jsx(Skeleton, { variant: "rectangular", height: "12px", width: "90%" })] }) :
                        _jsx(TextField, { InputProps: {
                                readOnly: true
                            }, multiline: true, maxRows: 6, sx: { width: "100%" }, value: modifiedTextArray[curIndex] }) }), _jsxs(Box, { sx: actionButtonsContainerSxProps, children: [modifiedTextArray.length !== 0 &&
                            _jsx(Button, { onClick: getText, disabled: isLoading, startIcon: _jsx(FontAwesomeIcon, { icon: faArrowRotateLeft }), children: "Rewrite" }), modifiedTextArray.length !== 0 &&
                            _jsx(Button, { onClick: () => onReplace(modifiedTextArray[curIndex]), disabled: isLoading, variant: "contained", startIcon: _jsx(FontAwesomeIcon, { icon: faCheck }), children: "Use" })] })] }) }));
};
const GradientBorderPaper = styled(Paper)(() => ({
    position: "relative",
    width: "376px",
    backgroundColor: "white",
    borderRadius: "8px",
    // Apply ::before pseudo-element for the gradient border
    "&::before": {
        content: "\"\"",
        position: "absolute",
        inset: 0,
        borderRadius: "8px",
        padding: "1px",
        background: "linear-gradient(114deg, #EB89F1 12.13%, #D47FEF 22.85%, #C175EE 33.17%, #AB6DEC 44.29%, #9365EB 55.41%, #775EE9 66.53%, #5358E7 77.25%, #0053E5 88.37%)",
        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        maskComposite: "exclude"
    }
}));
const titleContainerSxProps = {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    padding: "4px 16px"
};
const sxPaginationArrows = {
    display: "flex",
    alignItems: "center"
};
const textSkeletonContainerSxProps = {
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "16px 8px 16px 16px",
    border: "1px solid rgb(229, 234, 252)"
};
const actionButtonsContainerSxProps = {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    padding: "8px 16px"
};
//# sourceMappingURL=AiTextEditingPopover.js.map